import { db } from '../db/index.js';
import { getStore } from '../db/stores.js';
import {
  getAislesByStore,
  addAisle,
  getAisle,
  setAisleServerId,
} from '../db/aisles.js';
import {
  getListItems,
  searchArticles,
  getArticleTemplate,
  addListItem,
  toggleItem,
  deleteItem,
  updateItemQuantity,
  clearCheckedItems,
  setItemServerId,
} from '../db/articles.js';
import {
  pushCreateItem,
  pushUpdateItem,
  pushDeleteItem,
  pushCreateAisle,
  shareStore,
  unshareStore,
  getStoreMembers,
  removeMember,
} from '../api/user-sync.js';
import { isLoggedIn } from '../utils/auth.js';
import { isOnline, requireOnline } from '../utils/network.js';
import { renderHeader } from '../components/header.js';
import { renderAisleSection } from '../components/aisle-section.js';
import { createAutocomplete } from '../components/autocomplete.js';
import { openModal, closeModal } from '../components/modal.js';
import { createElement, clearElement, showToast } from '../utils/dom.js';
import { navigate } from '../router.js';
import { iconSettings, iconCart, iconPlus, iconTrash, iconShare, iconMic, iconShoppingBag, iconUser, iconX } from '../utils/icons.js';
import { isSpeechAvailable, listenVoice, parseVoiceInput } from '../utils/voice.js';

const NEW_AISLE_VALUE = '__new__';

export function resetStoreViewState() {
  // Placeholder pour compat avec app.js
}

/**
 * Affiche la vue liste de courses d'un magasin.
 * @param {HTMLElement} container
 * @param {number} storeId
 */
export async function renderStoreView(container, storeId) {
  clearElement(container);

  try {
    const store = await getStore(storeId);
    if (!store) {
      showToast('Magasin introuvable', 3000, 'error');
      navigate('/');
      return;
    }

    const role = store.role || 'owner';
    const isEditor = role === 'owner' || role === 'editor';

    const [items, aisles] = await Promise.all([
      getListItems(storeId),
      getAislesByStore(storeId),
    ]);

    const checkedCount = items.filter((i) => i.checked).length;
    const hasChecked = checkedCount > 0;
    const hasItems = items.length > 0;
    const hasBottomBar = hasItems;

    const pageClasses = [
      'page',
      'page-store',
      isEditor ? 'page--with-fab' : '',
      hasBottomBar ? 'page--with-bottom-bar' : '',
    ].filter(Boolean).join(' ');

    const page = createElement('div', { className: pageClasses });

    const headerActions = [];
    if (hasItems) {
      headerActions.push({
        icon: iconShoppingBag(20),
        label: 'Mode courses',
        onClick: () => navigate(`/store/${storeId}/shopping`),
      });
    }
    if (isEditor) {
      headerActions.push({
        icon: iconShare(20),
        label: 'Partager',
        onClick: () => openShareModal(store),
      });
    }
    headerActions.push({
      icon: iconSettings(20),
      label: 'Paramètres',
      onClick: () => navigate(`/store/${storeId}/settings`),
    });

    page.appendChild(renderHeader({
      title: store.name,
      backPath: '/',
      actions: headerActions,
    }));

    const refresh = () => renderStoreView(container, storeId);

    const callbacks = {
      onToggle: async (id) => {
        try {
          await toggleItem(id);
          const item = await db.listItems.get(id);
          if (item?.serverId) {
            const aisle = item.aisleId ? await getAisle(item.aisleId) : null;
            pushUpdateItem(item.serverId, item.name, aisle?.name ?? null, item.quantity, item.checked);
          }
          refresh();
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
      onDelete: isEditor ? async (id) => {
        if (!requireOnline()) return;
        try {
          const item = await db.listItems.get(id);
          await deleteItem(id);
          pushDeleteItem(item?.serverId);
          refresh();
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      } : null,
      onQuantityChange: isEditor ? async (id, qty) => {
        if (!requireOnline()) return;
        try {
          await updateItemQuantity(id, qty);
          const item = await db.listItems.get(id);
          if (item?.serverId) {
            const aisle = item.aisleId ? await getAisle(item.aisleId) : null;
            pushUpdateItem(item.serverId, item.name, aisle?.name ?? null, qty, item.checked);
          }
          refresh();
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      } : null,
    };

    if (items.length === 0) {
      page.appendChild(createElement('div', {
        className: 'empty-state',
        children: [
          createElement('span', { className: 'empty-state__icon', children: [iconCart(48)] }),
          createElement('h2', { className: 'empty-state__title', text: 'Liste vide' }),
          createElement('p', {
            className: 'empty-state__message',
            text: isEditor ? 'Votre liste est vide, ajoutez des articles !' : 'La liste est vide.',
          }),
        ],
      }));
    } else {
      const aislesContainer = createElement('div', { className: 'page-store__aisles' });

      const itemsByAisle = new Map();
      for (const aisle of aisles) {
        itemsByAisle.set(aisle.id, []);
      }
      const uncategorized = [];

      for (const item of items) {
        if (item.aisleId != null && itemsByAisle.has(item.aisleId)) {
          itemsByAisle.get(item.aisleId).push(item);
        } else {
          uncategorized.push(item);
        }
      }

      for (const aisle of aisles) {
        const aisleItems = itemsByAisle.get(aisle.id) ?? [];
        if (aisleItems.length > 0) {
          aislesContainer.appendChild(renderAisleSection(aisle, aisleItems, callbacks));
        }
      }

      if (uncategorized.length > 0) {
        aislesContainer.appendChild(renderAisleSection(null, uncategorized, callbacks));
      }

      page.appendChild(aislesContainer);
    }

    container.appendChild(page);

    if (isEditor) {
      const fab = createElement('button', {
        className: 'btn btn--fab',
        attrs: { type: 'button', 'aria-label': 'Ajouter un article' },
        children: [iconPlus(24)],
        events: {
          click: () => {
            if (!requireOnline()) return;
            openAddArticleModal(storeId, aisles, refresh);
          },
        },
      });
      container.appendChild(fab);
    }

    if (hasItems) {
      const actionBar = createElement('div', { className: 'page-store__actions' });

      if (hasChecked && isEditor) {
        actionBar.appendChild(createElement('button', {
          className: 'btn btn--ghost btn--danger-text',
          attrs: { type: 'button' },
          children: [iconTrash(16), ' Vider les cochés'],
          events: {
            click: async () => {
              if (!requireOnline()) return;
              try {
                const deletedItems = await clearCheckedItems(storeId);
                for (const item of deletedItems) {
                  if (item.serverId) pushDeleteItem(item.serverId);
                }
                refresh();
              } catch (err) {
                showToast(err.message || 'Erreur', 3000, 'error');
              }
            },
          },
        }));
      }

      actionBar.appendChild(createElement('span', {
        className: 'btn btn--ghost',
        attrs: { style: 'pointer-events: none; flex: 1; text-align: center;' },
        text: `${checkedCount}/${items.length} articles cochés`,
      }));

      container.appendChild(actionBar);
    }
  } catch (err) {
    showToast(err.message || 'Erreur lors du chargement', 3000, 'error');
    navigate('/');
  }
}

/**
 * Ouvre la modale de partage de store (nouveau système par compte).
 */
async function openShareModal(store) {
  if (!isLoggedIn()) {
    showToast('Connectez-vous pour partager', 3000, 'error');
    navigate('/login');
    return;
  }

  const serverId = store.serverId;
  if (!serverId) {
    showToast('Synchronisez d\'abord votre compte', 3000, 'error');
    return;
  }

  const content = createElement('div', { className: 'share-modal' });

  try {
    const members = await getStoreMembers(serverId);
    const ownerMember = members.find((m) => m.role === 'owner');
    const shareCode = store.shareCode;

    if (shareCode) {
      renderSharedState(content, store, shareCode, members);
    } else {
      renderUnsharedState(content, store);
    }
  } catch (err) {
    content.appendChild(createElement('p', {
      className: 'text-muted text-center',
      text: 'Erreur lors du chargement du partage',
    }));
  }

  openModal({ title: 'Partager la liste', content });
}

function renderSharedState(content, store, shareCode, members) {
  clearElement(content);

  const codeDisplay = createElement('div', {
    className: 'share-modal__code',
    attrs: { style: 'font-size: 1.5rem; font-weight: bold; letter-spacing: 0.2em; text-align: center; margin: var(--space-md) 0;' },
    text: shareCode,
  });

  const copyBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Copier le code',
    events: {
      click: async () => {
        try {
          await navigator.clipboard.writeText(shareCode);
          showToast('Code copié !', 2000, 'success');
        } catch {
          showToast('Impossible de copier', 3000, 'error');
        }
      },
    },
  });

  const otherMembers = members.filter((m) => m.role !== 'owner');
  const membersList = createElement('div', { className: 'share-modal__members' });

  if (otherMembers.length > 0) {
    membersList.appendChild(createElement('h3', {
      className: 'share-modal__members-title',
      text: `Membres (${otherMembers.length})`,
    }));

    for (const member of otherMembers) {
      const avatarEl = member.avatar_url
        ? createElement('img', {
            className: 'share-modal__member-avatar',
            attrs: { src: member.avatar_url, alt: member.pseudo, referrerpolicy: 'no-referrer' },
          })
        : createElement('div', {
            className: 'share-modal__member-avatar share-modal__member-avatar--initial',
            text: (member.pseudo?.[0] || '?').toUpperCase(),
          });

      const removeBtn = createElement('button', {
        className: 'btn btn--icon btn--icon-danger',
        attrs: { type: 'button', 'aria-label': 'Retirer' },
        children: [iconX(16)],
        events: {
          click: async () => {
            try {
              await removeMember(store.serverId, member.user_id);
              const newMembers = await getStoreMembers(store.serverId);
              renderSharedState(content, store, shareCode, newMembers);
              showToast('Membre retiré', 2000, 'success');
            } catch (err) {
              showToast(err.message || 'Erreur', 3000, 'error');
            }
          },
        },
      });

      membersList.appendChild(createElement('div', {
        className: 'share-modal__member',
        children: [
          avatarEl,
          createElement('div', {
            className: 'share-modal__member-info',
            children: [
              createElement('span', { className: 'share-modal__member-name', text: member.pseudo }),
              createElement('span', { className: 'share-modal__member-role text-muted', text: member.role === 'editor' ? 'Éditeur' : 'Lecteur' }),
            ],
          }),
          removeBtn,
        ],
      }));
    }
  }

  const stopBtn = createElement('button', {
    className: 'btn btn--ghost btn--danger-text btn--block',
    attrs: { type: 'button', style: 'margin-top: var(--space-sm);' },
    text: 'Arrêter le partage',
    events: {
      click: async () => {
        try {
          await unshareStore(store.serverId);
          await db.stores.update(store.id, { shareCode: null });
          closeModal();
          showToast('Partage arrêté', 2000, 'success');
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  content.append(
    createElement('p', {
      className: 'text-muted text-center',
      text: 'Partagez ce code — la personne doit se connecter avec Google pour y accéder.',
    }),
    codeDisplay,
    copyBtn,
    membersList,
    stopBtn,
  );
}

function renderUnsharedState(content, store) {
  clearElement(content);

  const roleSelect = createElement('select', {
    className: 'input',
    children: [
      createElement('option', { attrs: { value: 'editor' }, text: 'Éditeur — peut tout modifier' }),
      createElement('option', { attrs: { value: 'viewer' }, text: 'Lecteur — peut seulement cocher' }),
    ],
  });

  const shareBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Générer un code de partage',
    events: {
      click: async () => {
        shareBtn.disabled = true;
        shareBtn.textContent = 'Génération…';

        try {
          const data = await shareStore(store.serverId, roleSelect.value);
          await db.stores.update(store.id, { shareCode: data.share_code });
          store.shareCode = data.share_code;
          const members = await getStoreMembers(store.serverId);
          renderSharedState(content, store, data.share_code, members);
          showToast('Code généré !', 2000, 'success');
        } catch (err) {
          shareBtn.disabled = false;
          shareBtn.textContent = 'Générer un code de partage';
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  content.append(
    createElement('p', {
      className: 'text-muted text-center',
      text: 'Créez un code de partage pour collaborer. La personne devra se connecter avec Google.',
    }),
    createElement('div', {
      className: 'input-group',
      attrs: { style: 'margin: var(--space-md) 0;' },
      children: [
        createElement('label', { className: 'input-group__label', text: 'Rôle des invités' }),
        roleSelect,
      ],
    }),
    shareBtn,
  );
}

/**
 * Ouvre la modale d'ajout d'article.
 */
function openAddArticleModal(storeId, aisles, onRefresh) {
  if (!requireOnline()) return;
  let autocompleteInstance = null;
  let currentAisles = [...aisles];

  const nameInput = createElement('input', {
    className: 'input',
    attrs: { type: 'text', placeholder: 'Ex: Lait, Pain…', id: 'article-name-input' },
  });

  let micBtn = null;
  if (isSpeechAvailable()) {
    micBtn = createElement('button', {
      className: 'btn btn--icon btn--voice',
      attrs: { type: 'button', 'aria-label': 'Dicter un article' },
      children: [iconMic(20)],
      events: {
        click: async () => {
          try {
            micBtn.classList.add('btn--voice--listening');
            micBtn.disabled = true;

            const transcript = await listenVoice({
              onStart: () => { nameInput.placeholder = '🎤 Parlez maintenant…'; },
              onEnd: () => {
                nameInput.placeholder = 'Ex: Lait, Pain…';
                micBtn.classList.remove('btn--voice--listening');
                micBtn.disabled = false;
              },
            });

            if (transcript) {
              const { name, quantity } = parseVoiceInput(transcript);
              if (name) {
                nameInput.value = name;
                quantityInput.value = String(quantity);
                nameInput.dispatchEvent(new Event('input'));
                await applyTemplate(name);
                showToast(`"${transcript}" ✓`, 2000, 'success');
              }
            } else {
              showToast('Aucune voix détectée', 2000, 'error');
            }
          } catch (err) {
            micBtn.classList.remove('btn--voice--listening');
            micBtn.disabled = false;
            if (err.message === 'not-allowed') {
              showToast('Micro non autorisé', 3000, 'error');
            } else {
              showToast('Erreur vocale', 3000, 'error');
            }
          }
        },
      },
    });
  }

  const aisleSelect = createElement('select', { className: 'input' });
  const newAisleGroup = createElement('div', {
    className: 'input-group hidden',
    children: [
      createElement('label', { className: 'input-group__label', text: 'Nom du nouveau rayon' }),
    ],
  });
  const newAisleInput = createElement('input', {
    className: 'input',
    attrs: { type: 'text', placeholder: 'Ex: Fruits & Légumes' },
  });
  newAisleGroup.appendChild(newAisleInput);

  const quantityInput = createElement('input', {
    className: 'input',
    attrs: { type: 'number', min: '1', value: '1' },
  });

  function rebuildAisleSelect(selectedId = '') {
    aisleSelect.innerHTML = '';
    aisleSelect.appendChild(createElement('option', { attrs: { value: '' }, text: 'Sans rayon' }));
    for (const aisle of currentAisles) {
      aisleSelect.appendChild(createElement('option', {
        attrs: { value: String(aisle.id) },
        text: aisle.name,
      }));
    }
    aisleSelect.appendChild(createElement('option', {
      attrs: { value: NEW_AISLE_VALUE },
      text: '+ Nouveau rayon',
    }));
    aisleSelect.value = selectedId === null || selectedId === undefined ? '' : String(selectedId);
  }

  rebuildAisleSelect();

  aisleSelect.addEventListener('change', () => {
    newAisleGroup.classList.toggle('hidden', aisleSelect.value !== NEW_AISLE_VALUE);
    if (aisleSelect.value === NEW_AISLE_VALUE) newAisleInput.focus();
  });

  const nameInputRow = createElement('div', { className: 'input-row' });
  nameInputRow.appendChild(nameInput);
  if (micBtn) nameInputRow.appendChild(micBtn);

  const form = createElement('div', {
    className: 'add-article-form',
    children: [
      createElement('div', {
        className: 'input-group',
        children: [
          createElement('label', { className: 'input-group__label', text: 'Nom de l\'article' }),
          nameInputRow,
        ],
      }),
      createElement('div', {
        className: 'input-group',
        children: [
          createElement('label', { className: 'input-group__label', text: 'Rayon' }),
          aisleSelect,
          newAisleGroup,
        ],
      }),
      createElement('div', {
        className: 'input-group',
        children: [
          createElement('label', { className: 'input-group__label', text: 'Quantité' }),
          quantityInput,
        ],
      }),
    ],
  });

  async function applyTemplate(name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const template = await getArticleTemplate(storeId, trimmed);
      if (template) rebuildAisleSelect(template.aisleId ?? '');
    } catch { /* ignore */ }
  }

  autocompleteInstance = createAutocomplete({
    input: nameInput,
    onSearch: (query) => searchArticles(storeId, query),
    onSelect: (item) => rebuildAisleSelect(item.aisleId ?? ''),
  });

  nameInput.addEventListener('blur', () => applyTemplate(nameInput.value));

  const addBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Ajouter',
    events: {
      click: async () => {
        try {
          const name = nameInput.value.trim();
          if (!name) {
            showToast('Le nom est requis', 3000, 'error');
            return;
          }

          let aisleId = null;

          if (aisleSelect.value === NEW_AISLE_VALUE) {
            const newName = newAisleInput.value.trim();
            if (!newName) {
              showToast('Le nom du rayon est requis', 3000, 'error');
              return;
            }
            aisleId = await addAisle(storeId, newName);
            currentAisles = await getAislesByStore(storeId);

            const store = await getStore(storeId);
            const pushRes = await pushCreateAisle(store?.serverId, newName, 0);
            if (pushRes?.id) await setAisleServerId(aisleId, pushRes.id);

            rebuildAisleSelect(aisleId);
            newAisleGroup.classList.add('hidden');
            newAisleInput.value = '';
          } else if (aisleSelect.value) {
            aisleId = Number(aisleSelect.value);
          }

          const quantity = parseInt(quantityInput.value, 10) || 1;
          const localItemId = await addListItem({ storeId, name, aisleId, quantity });

          const store = await getStore(storeId);
          const aisleName = aisleId ? (await getAisle(aisleId))?.name : null;
          const pushResult = await pushCreateItem(store?.serverId, name, aisleName, quantity, false);
          if (pushResult?.id) {
            await setItemServerId(localItemId, pushResult.id);
          }

          onRefresh();

          nameInput.value = '';
          quantityInput.value = '1';
          rebuildAisleSelect(aisleId);
          nameInput.focus();
          showToast('Article ajouté', 1500, 'success');
        } catch (err) {
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  openModal({
    title: 'Ajouter un article',
    content: form,
    footer: addBtn,
    onClose: () => autocompleteInstance?.destroy(),
  });

  nameInput.focus();
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') addBtn.click();
  });
}
